import React, { useState } from "react";
import { FaDownload, FaBrush, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import useEditorActions from "../utils/useEditorActions";
import CommonSeoContent from "../components/CommonSeoContent";
import yaml from "js-yaml";

const JsonToYaml = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  // JSON → YAML conversion
  const handleConvert = () => {
    try {
      const json = JSON.parse(inputCode);
      const yamlStr = yaml.dump(json, { indent: 2 });
      setOutputCode(yamlStr);
    } catch (e) {
      setOutputCode("❌ Invalid JSON: " + e.message);
    }
  };

  return (
    <div className="formatter-container">
      <h1>JSON ➝ YAML Converter</h1>

      {/* Toolbar */}
      <div className="toolbar">
        <button onClick={handleConvert} className="btn primary">
          <FaBrush /> Convert
        </button>
        <button
          onClick={() => handleDownload("yaml", "converted-yaml")}
          className="btn success"
        >
          <FaDownload /> Download
        </button>
        <button onClick={handleClear} className="btn danger">
          <FaTrash /> Clear
        </button>
      </div>

      {/* Editors */}
      <div className="editor-panels">
        <JsonEditorPanel
          title="Input JSON"
          language="json"
          code={inputCode}
          setCode={setInputCode}
        />
        <div className="editor-section-empty-div"></div>
        <JsonEditorPanel
          title="Output YAML"
          language="yaml"
          code={outputCode}
          readOnly
        />
      </div>
	  

		  <CommonSeoContent
		  title="JSON to YAML Converter & Formatter"
		  description="Convert JSON data into YAML Document instantly with jsonconverter.org. Supports nested JSON and produces clean Go code for developers."
		  keywords="JSON to Yaml, json to yaml code generator, json to yaml code converter, online YAML converter, yaml formatter, jsonconverter.org, json to yaml processor"
		  author="jsonconverter.org"
		  heading="Why Use JSON to YAML Converter?"
		  whyUseText="Our JSON to YAML Converter makes it easy to transform your JSON data into clean, readable YAML format. Whether you're configuring cloud services, writing CI/CD pipelines, or simply prefer YAML's cleaner syntax, this tool helps you convert data accurately and effortlessly. It supports complex nested objects and arrays, preserving the structure and meaning of your original JSON. Because everything runs directly in your browser, there's no need to install software or worry about data privacy your input never leaves your device. It’s fast, secure, and completely free, making it ideal for developers, DevOps engineers, system admins, and data professionals working with structured data formats."
		  howToUseSteps={[
			"Paste your JSON in the input editor.",
			"Click the Convert button",
			"View your converted YAML output instantly",
			"Copy or download the YAML for use in your projects.",
		  ]}
		  features={[
			"Supports deeply nested JSON objects and arrays",
			"Converts data while maintaining hierarchy and accuracy",
			"Outputs clean, easy-to-read YAML with proper indentation",
			"Fully browser-based — no data is sent to a server",
			"No installation, registration, or login required",
			"Option to download the YAML file for offline use",
		  ]}
		  inputTitle="Input JSON:"
		  inputExample={`
{
  "name": "John Doe",
  "age": 30,
  "emails": ["john@example.com"],
  "isActive": true,
  "profile": {
	"bio": "Developer",
	"website": "https://jsonconverter.org"
  }
}`}
		  outputTitle="Output YAML:"
		  outputExample={`
name: John Doe
age: 30
emails:
  - john@example.com
isActive: true
profile:
  bio: Developer
  website: https://jsonconverter.org`}
		  callToAction="Start converting your JSON to YAML instantly — no signup required."
		/> 
    </div>
  );
};

export default JsonToYaml;
